import React, { useCallback } from 'react';
import Modal from '#/common/components/Modal';
import TMMButton from '#/common/components/TMMButton';
import { v4 as uuidv4 } from 'uuid';
import useGetAttachmentUploadUrl from '#/common/components/AttachmentUpload/hooks/useAttachmentUploadUrlMutation';
import useAttachmentCreate from '#/common/components/AttachmentUpload/hooks/useAttachmentCreateMutation';
import useImportAssetFileMutation from '../hooks/useImportAssetFileMutation';
import { ImportFileOutputData, Tenant } from '#/graphql/types';
import { unknownMappingValue } from '#/common/components/ColumnMapper/ColumnMapper';

interface ImportColumnConfig {
  column?: string; // Set the column from the CSV file
  fixedValue?: unknown; // If populated this value is used for all records, column value is ignored
  mandatory?: boolean;
  unique?: boolean;
  type?: 'string' | 'number' | 'boolean' | 'date' | 'datetime' | 'ref';
  ref?: {
    table: string;
    column: string;
  };
}

export type ImportConfig = Record<string, ImportColumnConfig>;

interface SubmitModalProps {
  visible: boolean;
  uploadedFile: File | null;
  mapping: Record<string, string>;
  uniqueField: string;
  selectedTenant?: Tenant;
  onHiding: () => void;
  onImportStart: () => void;
  onSuccess: (importResult: ImportFileOutputData) => void;
  onFailed: () => void;
}

const SubmitModal: React.FC<SubmitModalProps> = ({
  visible,
  uploadedFile,
  mapping,
  uniqueField,
  selectedTenant,
  onHiding,
  onImportStart,
  onSuccess,
  onFailed,
}) => {
  const { getAttachmentUploadUrls } = useGetAttachmentUploadUrl();
  const { createAttachment } = useAttachmentCreate();
  const { importAssetFile } = useImportAssetFileMutation();

  // Generate the import config based on the mapping
  const generateImportConfig = useCallback((): string => {
    if (!selectedTenant || !mapping || Object.keys(mapping).length === 0) {
      return '';
    }

    const isColumnMapped = (field: string) =>
      !!mapping[field] && mapping[field] !== unknownMappingValue.key;

    // Base config structure that mirrors backend's assetImportConfig
    const config: ImportConfig = {
      // Fixed values
      tenantId: {
        fixedValue: selectedTenant.id,
        mandatory: true,
        unique: true,
        type: 'ref',
        ref: {
          table: 'tenants',
          column: 'name',
        },
      },
      // No need to send State as fixed 'installed' value because if empty it will be set such as default on backend side.
      // state: { fixedValue: 'installed', column: 'state', type: 'string' },

      // Mapped values (column will be set based on user mapping)
      // Mandatory fields
      assetTag: {
        column: mapping['asset_tag'] || unknownMappingValue.key,
        mandatory: true,
        unique: uniqueField === 'asset_tag',
        type: 'string',
      },
      serialNumber: {
        column: mapping['serial_number'] || unknownMappingValue.key,
        mandatory: true,
        unique: uniqueField === 'serial_number',
        type: 'string',
      },
      assetTypeId: {
        column: mapping['asset_type'] || unknownMappingValue.key,
        mandatory: true,
        type: 'ref',
        ref: {
          table: 'asset_types',
          column: 'name',
        },
      },
      makeId: {
        column: mapping['manufacturer'] || unknownMappingValue.key,
        mandatory: true,
        type: 'ref',
        ref: {
          table: 'manufacturers',
          column: 'name',
        },
      },
      modelId: {
        column: mapping['model'] || unknownMappingValue.key,
        mandatory: true,
        type: 'ref',
        ref: {
          table: 'models',
          column: 'name',
        },
      },
      description: {
        column: mapping['description'] || unknownMappingValue.key,
        mandatory: true,
        type: 'string',
      },
      locationId: {
        column: mapping['location_path'] || unknownMappingValue.key,
        mandatory: true,
        type: 'ref',
        ref: {
          table: 'locations',
          column: 'name',
        },
      },
    };

    // Optional fields
    if (isColumnMapped('install_date')) {
      config.installDate = {
        column: mapping['install_date'] || unknownMappingValue.key,
        type: 'date',
      };
    }
    if (isColumnMapped('condition')) {
      config.condition = {
        column: mapping['condition'] || unknownMappingValue.key,
        type: 'string',
      };
    }
    if (isColumnMapped('acquisition_cost_value')) {
      config.acquisitionCostValue = {
        column: mapping['acquisition_cost_value'] || unknownMappingValue.key,
        type: 'number',
      };
    }
    if (isColumnMapped('maintenance_cost_value')) {
      config.maintenanceCostValue = {
        column: mapping['maintenance_cost_value'] || unknownMappingValue.key,
        type: 'number',
      };
    }
    if (isColumnMapped('useful_life')) {
      config.usefulLife = {
        column: mapping['useful_life'] || unknownMappingValue.key,
        type: 'number',
      };
    }

    return JSON.stringify(config);
  }, [uniqueField, mapping, selectedTenant]);

  // Upload CSV file as an attachment and get the fileId to send to the backend for processing
  const uploadInputFile = useCallback(
    async (file: File | null): Promise<string | undefined> => {
      if (!file) return;

      const relatedTable = 'data_import'; // TODO: No such table yet. Replace with correct related table when created.
      const relatedItem = uuidv4(); // TODO: Using a random UUID. Replace with correct related item ID when table is created.

      const uploadInfos = await getAttachmentUploadUrls([
        {
          fileName: file.name,
          size: file.size,
          contentType: file.type,
          relatedTable,
          relatedItem,
          type: 'general',
        },
      ]);

      const uploadInfo = Array.isArray(uploadInfos)
        ? uploadInfos.find((u: any) => u && u.uploadUrl && u.uploadName)
        : undefined;

      if (!uploadInfo?.uploadUrl || !uploadInfo?.uploadName) {
        throw new Error('Failed to get upload URL');
      }

      const uploadResponse = await fetch(uploadInfo.uploadUrl as string, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });

      if (!uploadResponse.ok) {
        throw new Error(`Failed to upload file: ${uploadResponse.statusText}`);
      }

      const attachment = await createAttachment({
        fileName: String(uploadInfo.uploadName).split('/').pop() || '',
        size: file.size,
        mimeType: file.type,
        relatedTable,
        relatedItem,
        type: 'general',
        path: uploadInfo.uploadName as string,
        originalFileName: uploadInfo.originalFileName as string,
      });

      if (!attachment || !attachment.id || !attachment.downloadUrl) {
        throw new Error('Failed to create attachment record');
      }

      return attachment.id;
    },
    [getAttachmentUploadUrls, createAttachment],
  );

  const insertNewAssets = useCallback(
    async (skipExisting: boolean) => {
      onHiding();
      onImportStart();

      try {
        // Upload the file to S3 bucket and get the fileId
        const fileId = await uploadInputFile(uploadedFile);
        if (!fileId) {
          throw new Error('Error uploading file');
        }

        // Call the actual file import on backend side
        const importConfig = generateImportConfig();
        const importResult = await importAssetFile({
          importConfig,
          fileId,
          skipExisting,
        });

        if (!importResult) {
          throw new Error('Import failed');
        }

        onSuccess(importResult);
      } catch (error) {
        onFailed();
        return;
      }
    },
    [selectedTenant, uploadedFile, mapping, generateImportConfig],
  );

  const handleOverwriteSkip = useCallback(async (): Promise<void> => {
    await insertNewAssets(true);
  }, [insertNewAssets]);

  const handleOverwriteConfirm = useCallback(async (): Promise<void> => {
    await insertNewAssets(false);
  }, [insertNewAssets]);

  return (
    <Modal
      visible={visible}
      title="Overwrite Data of Existing Assets"
      width={640}
      height="auto"
      onHiding={onHiding}
      content={
        <>
          <div>
            If there are already existing asset records do you want to overwrite them with the new
            data from this file or skip them?
          </div>
          <div>Overwriting the records means that all changes made before will be lost.</div>
        </>
      }
      actions={[
        <TMMButton key="cancel" label="Skip" type="normal" onClick={handleOverwriteSkip} />,
        <TMMButton
          key="ok"
          label="Overwrite Data"
          type="submit"
          onClick={handleOverwriteConfirm}
        />,
      ]}
    />
  );
};

export default SubmitModal;
